<script>

    import { util as helperUtil } from '@/Helpers'

    export function getContactsRequiringEmailConfirmation(contacts = []) {
        return (contacts ?? []).filter((contact) => {
            const hasSignerRole = contact?.primary_contact || contact?.billing_contact
            const email = contact?.emails && contact.emails[0] ? String(contact.emails[0]).trim() : ''

            return hasSignerRole && (!email || !helperUtil.isValidEmail(email))
        })
    }

    const SERVICE_DETAIL_FIELD_DEFINITIONS = {
        service_class: { label: 'Class' },
        variant: { label: 'Variant', format: formatServiceDetailVariant },
        hourly_rate: { label: 'Hourly Rate', format: formatServiceDetailCurrency },
        daily_rate: { label: 'Daily Rate', format: formatServiceDetailCurrency },
        nurse_assessment_rate: { label: 'Nurse Assessment Rate', format: formatServiceDetailCurrency },
        medication_hourly_rate: { label: 'Medication Hourly Rate', format: formatServiceDetailCurrency },
        registration_fee: { label: 'Registration Fee', format: formatServiceDetailCurrency },
        permanent_placement_fee: { label: 'Permanent Placement Fee', format: formatPermanentPlacementFee },
        aob_insurance_company_name: { label: 'Insurance Company Name' },
        aob_insurance_policy_number: { label: 'Insurance Policy Number' },
        aob_insurance_claim_number: { label: 'Insurance Claim Number' },
        state: { label: 'State' },
        uploader_contact_user_id: { label: 'Uploader', format: (value, _serviceDetails, resolveContactName) => resolveContactName(value) },
    }

    function formatPermanentPlacementFee(value) {
        const amount = Number(value)

        if (Number.isNaN(amount)) {
            return String(value)
        }

        return `${amount}%`
    }

    function formatServiceDetailCurrency(value) {
        const amount = Number(value)

        if (Number.isNaN(amount)) {
            return String(value)
        }

        return `$${amount.toFixed(2)}`
    }

    function formatServiceDetailVariant(value) {
        if (value === null || value === undefined || value === '') {
            return ''
        }

        const option = SERVICE_VARIANT_OPTIONS.find((item) => item.id === value)

        return option?.name ?? String(value)
    }

    function humanizeServiceDetailKey(key) {
        return key
            .split('_')
            .filter(Boolean)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ')
    }

    function formatServiceDetailValue(value) {
        if (Array.isArray(value)) {
            return value.map(item => formatServiceDetailValue(item)).join(', ')
        }

        if (value && typeof value === 'object') {
            return JSON.stringify(value)
        }

        return String(value)
    }

    function buildServiceDetailEntries(serviceDetails, resolveContactName) {
        if (!serviceDetails || typeof serviceDetails !== 'object') {
            return []
        }

        const entries = []
        const usedKeys = new Set()

        Object.entries(SERVICE_DETAIL_FIELD_DEFINITIONS).forEach(([key, definition]) => {
            const value = serviceDetails[key]

            if (value === null || value === undefined || value === '') {
                return
            }

            usedKeys.add(key)
            entries.push({
                key,
                label: definition.label,
                value: definition.format ? definition.format(value, serviceDetails, resolveContactName) : formatServiceDetailValue(value),
            })
        })

        Object.entries(serviceDetails).forEach(([key, value]) => {
            if (usedKeys.has(key) || value === null || value === undefined || value === '') {
                return
            }

            entries.push({
                key,
                label: humanizeServiceDetailKey(key),
                value: formatServiceDetailValue(value),
            })
        })

        return entries
    }

    export function getBundleServiceDetailSummary(bundle, options = {}) {
        const resolveDocumentTitle = options.resolveDocumentTitle ?? ((doc) => doc?.document_template_id || 'Document')
        const resolveContactName = options.resolveContactName ?? (() => 'Unknown Contact')
        const sortDocuments = options.sortDocuments ?? ((documents) => documents)

        if (!bundle || !Array.isArray(bundle.documents)) {
            return []
        }

        return sortDocuments([...bundle.documents]).reduce((sections, doc) => {
            const serviceDetails = doc?.signature_additional_document_data?.service_details
            const fields = buildServiceDetailEntries(serviceDetails, resolveContactName)

            if (fields.length === 0) {
                return sections
            }

            sections.push({
                id: doc?.id ?? resolveDocumentTitle(doc),
                title: resolveDocumentTitle(doc),
                fields,
            })

            return sections
        }, [])
    }

</script>

<script setup>

    import { ref, computed, useTemplateRef, onUnmounted, watch, provide } from 'vue'
    import { storeToRefs } from 'pinia'

    import { ArrowRightIcon, ExclamationTriangleIcon, EyeIcon, ArrowDownTrayIcon, TagIcon, TrashIcon, XCircleIcon } from '@heroicons/vue/20/solid'
    import ProgressBar from '@/Components/ProgressBar.vue'
    import Loader from '@/Components/App/Loader.vue'

    import LatteClientSignatureRequestDialog from '@/Components/Client/LatteClientSignatureRequestDialog.vue'
    import DocumentTagModal from '@/Components/Client/DocumentTagModal.vue'
    import { SERVICE_VARIANT_OPTIONS } from '@/Stores'
    import { useJobRunner } from '@/Helpers/jobRunner'
    import { useClientSingleStore, useConfigStore, useStaffStore, useNotificationStore, useClientDocumentStore, useViewStore } from '@/Stores'
    import { util } from '@/Helpers/util'
    import { IconDocumentArrowUp16Regular, IconDocumentEdit16Regular, IconDocumentError16Filled, IconDocumentTextClock20Regular, IconSignature16Regular, IconQuestionCircle12Filled } from '@iconify-prerendered/vue-fluent'
    import moment from 'moment'
    import { useDropZone } from '@vueuse/core'
    import Modal from '@/Components/App/Modal.vue'
    import ConfirmationModal from '@/Components/App/ConfirmationModal.vue'
    import PrimaryButton from '@/Components/Forms/PrimaryButton.vue'
    import SecondaryButton from '@/Components/Forms/SecondaryButton.vue'

    const configStore = useConfigStore()
    const staffStore = useStaffStore()
    const clientSingleStore = useClientSingleStore()
    const clientDocumentStore = useClientDocumentStore()
    const viewStore = useViewStore()

    const tagModalOpen = ref(false)
    const selectedDocument = ref(null)
    const selectedBundle = ref(null)
    const tagSelection = ref([])
    const tagSaveLoading = ref(false)
    const hoveredRowData = ref(null)
    const pinnedRowData = ref(null)
    const isRowPinned = ref(false)
    const loading = ref(true)

    const jobRunner = useJobRunner('document-tag')
    const subscriberIds = []

    // subscribe to retry/finished events for user feedback
    subscriberIds.push(jobRunner.subscribe('addedRetryJob', function(errorCode, retryTime) {
        notificationStore.add('Error Saving Changes', 'error', `Retrying automatically... (${retryTime}/20)`, 15000)
        clientSingleStore.stopInactivityTimeout()
    }))
    subscriberIds.push(jobRunner.subscribe('reachedMaxRetry', function() {
        const maxRetryDialogId = notificationStore.add('Error Saving Changes', 'error', 'Make an edit to try again.', 7 * 24 * 60 * 60 * 1000)
        clientSingleStore.setMaxRetryDialogId(maxRetryDialogId)
    }))

    onUnmounted(() => {
        try {
            jobRunner.unsubscribe('addedRetryJob', subscriberIds[0])
            jobRunner.unsubscribe('reachedMaxRetry', subscriberIds[1])
        } catch (e) {
            // ignore
        }
    })

    const signatureRequestOpen = ref(false)
    const showMissingSignerEmailConfirmation = ref(false)
    const firstMissingSignerEmailContactId = ref(null)
    const confirmingDocumentDeletion = ref(false)
    const documentToDelete = ref(null)
    const bundleToDelete = ref(null)
    const confirmingCancelSigning = ref(false)
    const bundleToCancel = ref(null)
    const missingSignerNames = ref([])
    const uploadInProgress = ref(false)
    const uploadProgress = ref(0)
    const fileInput = useTemplateRef('fileInput')
    const missingDocFileInput = useTemplateRef('missingDocFileInput')
    const inlineDropZoneEl = useTemplateRef('inlineDropZoneEl')
    const belowModuleDropZoneEl = ref(null)
    const pendingMissingDocTag = ref(null)
    const pendingCreateClientId = ref(null)
    const tagModalInitialTags = ref([])
    const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'tif', 'tiff', 'png', 'gif', 'webp', 'txt', 'rtf']

    const previewableExtensions = ['pdf', 'jpg', 'jpeg', 'tif', 'tiff', 'png', 'gif', 'webp', 'txt']

    const props = defineProps({
        createSignatureRequestButtonRef: {
            type: Object,
            default: null,
        },
    })

    // Template refs for tour
    const documentsListAreaRef = ref(null)
    const documentsDetailAreaRef = ref(null)
    const signatureDialogRef = useTemplateRef('signature-request-dialog')

    // Onboarding tour state
    const tourWasSignatureDialogOpen = ref(false)

    // Centralized handler for managing dialog state during tour
    async function handleTourStepChange(index) {
        // index is 0-based
        // Steps 0-1: just viewing (list, detail)
        // Step 2: upload area
        // Step 3: create button
        // Steps 4-9: dialog should be open (agency, care recipients, primary signer, additional signers, hipaa signer, documents)
        const shouldDialogBeOpen = index >= 4 && index <= 9

        if (shouldDialogBeOpen && !signatureRequestOpen.value) {
            signatureRequestOpen.value = true
        } else if (!shouldDialogBeOpen && signatureRequestOpen.value) {
            signatureRequestOpen.value = false
        }
    }

    function getTourSteps() {
        // Determine which upload area to target based on visibility
        const getUploadAreaElement = () => {
            return showInlineUpload.value ? inlineDropZoneEl.value : belowModuleDropZoneEl.value
        }

        return [
            {
                attachTo: { element: () => documentsListAreaRef.value },
                content: {
                    title: 'Document List',
                    description: 'All client documents are organized here. You can see pending signature requests, completed documents, and manually uploaded files.',
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
            {
                attachTo: { element: () => documentsDetailAreaRef.value },
                content: {
                    title: 'Document Details',
                    description: 'Click any document to view detailed information including signers, service details, and signing status.',
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
            {
                attachTo: { element: getUploadAreaElement },
                content: {
                    title: 'Upload Documents',
                    description: `Click or drag and drop files to upload documents. Accepted file types: ${allowedExtensions.join(', ').toUpperCase()}.`,
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
            {
                attachTo: { element: () => props.createSignatureRequestButtonRef },
                content: {
                    title: 'Create Signature Request',
                    description: 'Click this button to start a new signature request workflow. You can select documents and configure signers.',
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
            {
                attachTo: { element: () => signatureDialogRef.value?.getAgencySectionRef() },
                content: {
                    title: 'Agency Selection',
                    description: 'Select which LifeWorx agency will sign the documents and the staff member who will be the signer.',
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
            {
                attachTo: { element: () => signatureDialogRef.value?.getCareRecipientsSectionRef() },
                content: {
                    title: 'Care Recipients',
                    description: 'Select which contacts are receiving care services. These are typically the clients themselves.',
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
            {
                attachTo: { element: () => signatureDialogRef.value?.getPrimarySignerSectionRef() },
                content: {
                    title: 'Primary Signer',
                    description: 'The main person responsible for signing service, payment, and placement agreements. This contact will receive the document bundle first and be required to complete certain additional fields during the signing process.',
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
            {
                attachTo: { element: () => signatureDialogRef.value?.getAdditionalSignersSectionRef() },
                content: {
                    title: 'Additional Signers',
                    description: 'Additional contacts who need to sign service and payment agreements alongside the primary signer. They will receive the documents after the primary signer.',
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
            {
                attachTo: { element: () => signatureDialogRef.value?.getHipaaSignerSectionRef() },
                content: {
                    title: 'LTCI/Healthcare Auth Signer',
                    description: 'The authorized contact who can sign LTCI (Long-Term Care Insurance) and healthcare authorization documents. This is typically required for healthcare services or insurance billing.',
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
            {
                attachTo: { element: () => signatureDialogRef.value?.getTemplatesSectionRef() },
                content: {
                    title: 'Documents & Service Details',
                    description: 'Select which documents to include in the signature request and configure service details like rates and agreement terms.',
                },
                on: {
                    beforeStep: async ({ index }) => {
                        await handleTourStepChange(index)
                    },
                },
            },
        ]
    }

    function startDocumentsTour(startAtStep = null) {
        tourWasSignatureDialogOpen.value = signatureRequestOpen.value

        if (startAtStep != null) {
            // Start at specified step
            viewStore.startTour(getTourSteps(), handleTourFinish, startAtStep)
        } else if (signatureRequestOpen.value) {
            // Start at step 5 (index 4) if dialog is already open (first dialog step after new upload)
            viewStore.startTour(getTourSteps(), handleTourFinish, 4)
        } else {
            // Start from beginning
            viewStore.startTour(getTourSteps(), handleTourFinish)
        }
    }

    function handleTourFinish() {
        // Close the dialog if it wasn't open before the tour started
        if (!tourWasSignatureDialogOpen.value && signatureRequestOpen.value) {
            signatureRequestOpen.value = false
        }
    }

    const { isOverDropZone: isOverInlineDropZone } = useDropZone(inlineDropZoneEl, { onDrop: handleFileDrop })
    const { isOverDropZone: isOverBelowDropZone } = useDropZone(belowModuleDropZoneEl, { onDrop: handleFileDrop })

    const emit = defineEmits(['missing-primary-email-confirmation-closed'])

    const notificationStore = useNotificationStore()

    const { config } = storeToRefs(configStore)
    const { activeStaffs } = storeToRefs(staffStore)
    const { client } = storeToRefs(clientSingleStore)

    const hasClientData = computed(() => {
        return client.value && (client.value.id || client.value.uuid)
    })

    watch(() => client.value, (val) => {
        loading.value = !(val && (val.id || val.uuid))
    }, { immediate: true })

    // headerEl not needed when wrapped in LatteStyledContainer

    const agencyOptions = computed(() => config.value.agency ?? [])
    const documentOptions = computed(() => config.value.documents.groups ?? [])
    const flatDocuments = computed(() => {
        const docs = config.value.documents.groups || []
        const flat = []
        const flattenDocs = (items) => {
            items.forEach(item => {
                if (item.documents && Array.isArray(item.documents)) {
                    flattenDocs(item.documents)
                } else {
                    if (item.use_as_tag) {
                        flat.push(item)
                    }
                }
            })
        }
        flattenDocs(docs)
        return flat
    })
    const documentTags = computed(() => config.value.documents.tags ?? [])
    const staffOptions = computed(() => activeStaffs.value ?? [])

    // Document bundles returned from API (when requesting additional_fields = ['documents'])
    const clientDocumentBundles = computed(() => client.value?.document_bundles ?? [])

    const hasAnyDocuments = computed(() => Array.isArray(clientDocumentBundles.value) && clientDocumentBundles.value.length > 0)

    const documentTemplatesMap = computed(() => {
        const map = {}
        const docs = Array.isArray(flatDocuments.value) ? flatDocuments.value : (flatDocuments.value ? Object.values(flatDocuments.value) : [])
        docs.forEach(d => {
            if (d && d.id) map[d.id] = d
        })
        return map
    })

    const hasActiveTemplateInClientDocumentBundles = (templateName) => {
        if (!templateName) return false
        return clientDocumentBundles.value.some(bundle =>
            (bundle.documents || []).some(doc =>
                (doc.tags ?? []).includes(templateName) &&
                !['document.voided', 'document.declined'].includes(bundle.signature_pandadoc_document_status) &&
                !bundle.signature_canceled_at
            )
        )
    }

    const requiredTemplateNames = computed(() => {
        const names = []
        const cl = client.value || {}

        // Documents are only required while a client is actively being served (or
        // once completed, only if it was a placement) - matches DocumentsCompletionChecker.
        const requiresDocuments = ['service_starting', 'ongoing'].includes(cl.disposition) ||
            (cl.disposition === 'completed' && cl.service_level === 'placement')

        if (!requiresDocuments) {
            return names
        }

        if (cl.service_level === 'placement') {
            names.push('Placement Agreement')
        } else if (['service_starting', 'ongoing'].includes(cl.disposition)) {
            names.push('Service Agreement')
            names.push('Payment Agreement')
        }

        const payTypes = Array.isArray(cl.pay_types) ? cl.pay_types : []
        if (payTypes.includes('ltc')) {
            names.push('LTCI Authorization')
            names.push('LTCI Assignment of Benefits')
        }

        if (cl.is_healthcare) {
            names.push('Healthcare HIPAA Release')
            names.push('Healthcare Patient Bill of Rights')
        }

        return names
    })

    const missingDocuments = computed(() => {
        const map = new Map()

        requiredTemplateNames.value.forEach(name => {
            if (!hasActiveTemplateInClientDocumentBundles(name)) {
                const key = `${name.replace(/\s+/g, '-').toLowerCase()}`
                map.set(key, { id: key, name: name, template_name: name })
            }
        })

        return Array.from(map.values())
    })

    const completeStatuses = ['document.completed', 'document.declined']
    const expiredStatuses = ['document.voided']

    const pendingSignatureBundles = computed(() => {
        return clientDocumentBundles.value.filter(b =>
            b.uses_signature_workflow &&
            !completeStatuses.includes(b.signature_pandadoc_document_status) &&
            !expiredStatuses.includes(b.signature_pandadoc_document_status) &&
            !b.signature_canceled_at
        ).sort((a, b) => {
            return b.id - a.id
        })
    })

    const pendingDocumentsCount = computed(() => {
        return pendingSignatureBundles.value.reduce((count, b) => {
            return count + (b.documents ? b.documents.length : 0)
        }, 0)
    })

    const documentStatus = computed(() => {
        if (missingDocuments.value.length > 0) {
            return 'alert'
        }
        if (pendingDocumentsCount.value > 0) {
            return 'warn'
        }
        return null
    })

    const completedDocumentBundles = computed(() => {
        return clientDocumentBundles.value.filter(b =>
            b.uses_signature_workflow &&
            b.signature_pandadoc_document_status === 'document.completed'
        ).sort((a, b) => {
            return b.id - a.id
        })
    })

    const declinedDocumentBundles = computed(() => {
        return clientDocumentBundles.value.filter(b =>
            b.uses_signature_workflow &&
            b.signature_pandadoc_document_status === 'document.declined'
        ).sort((a, b) => {
            return b.id - a.id
        })
    })

    const expiredDocumentBundles = computed(() => {
        return clientDocumentBundles.value.filter(b =>
            b.uses_signature_workflow &&
            (
                expiredStatuses.includes(b.signature_pandadoc_document_status) ||
                b.signature_canceled_at
            )
        ).sort((a, b) => {
            return b.id - a.id
        })
    })

    const manuallyUploadedBundles = computed(() => {
        return clientDocumentBundles.value.filter(b => !b.uses_signature_workflow).sort((a, b) => {
            return b.id - a.id
        })
    })

    const handlePreviewDocument = async (doc) => {
        if (!doc || !doc.preview_url) return
        try {
            await util.checkUrlExists(doc.preview_url)
            window.open(doc.preview_url, '_blank')
        } catch (error) {
            notificationStore.add('Preview Failed', 'error', error.message)
        }
    }

    function openTagModal(doc, bundle, initialTags = []) {
        selectedDocument.value = doc
        selectedBundle.value = bundle
        tagSelection.value = Array.isArray(doc.tags) ? doc.tags.slice() : []
        tagModalInitialTags.value = initialTags
        tagModalOpen.value = true
    }

    function closeTagModal() {
        tagModalOpen.value = false
        selectedDocument.value = null
        selectedBundle.value = null
        tagSelection.value = []
        tagModalInitialTags.value = []
        tagSaveLoading.value = false
    }

    function updateBundleDocument(bundleId, documentId, newDoc) {
        if (!client.value || !client.value.document_bundles) return
        const bundlesArr = client.value.document_bundles || []
        const b = bundlesArr.find(x => x.id === bundleId)
        if (!b || !b.documents) return
        const idx = b.documents.findIndex(d => d.id === documentId)
        if (idx !== -1) {
            b.documents.splice(idx, 1, newDoc)
        }
    }

    // Use shared util.arraysEqualAsSet (imported as util)

    async function submitTagUpdate(newTags) {
        if (!selectedDocument.value || !selectedBundle.value) return
        // No-op if nothing changed
        if (util.arraysHaveSameUniqueElements(newTags, selectedDocument.value.tags)) {
            return
        }

        tagSaveLoading.value = true

        // capture IDs so the background job can complete even if the modal is closed
        const bundleId = selectedBundle.value?.id
        const documentId = selectedDocument.value?.id
        const clientId = client.value?.id || pendingCreateClientId.value
        try {
            const resp = await clientDocumentStore.updateDocumentTags(clientId, bundleId, documentId, newTags)
            const updated = resp && resp.data ? resp.data : resp
            updateBundleDocument(bundleId, documentId, updated)
            // Ensure the currently selectedDocument ref reflects the updated document
            if (selectedDocument.value && selectedDocument.value.id === documentId) {
                selectedDocument.value = updated
            }
            notificationStore.add('Document tags updated')
        } catch (err) {
            const details = err?.response?.data?.message || err?.message || 'Unknown error'
            notificationStore.add('Failed to update tags', 'error', details)
        } finally {
            tagSaveLoading.value = false
        }
    }

    function handleTagModalClose() {
        if (tagSaveLoading.value) return

        const createdClientId = pendingCreateClientId.value
        closeTagModal()

        if (!createdClientId) return

        pendingCreateClientId.value = null
        const { fe_generated, ...clientData } = client.value
        clientSingleStore.hydrate({ ...clientData, id: createdClientId })
    }

    const openSignatureRequestDialog = (skipMissingEmailCheck = false) => {
        // If any signer contacts are missing a valid primary email, show confirmation modal
        if (!skipMissingEmailCheck) {
            const contacts = client.value?.contacts ?? []
            const missing = getContactsRequiringEmailConfirmation(contacts)
            if (missing && missing.length > 0) {
                missingSignerNames.value = missing.map(c => [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || 'Unknown')
                firstMissingSignerEmailContactId.value = missing[0].id
                showMissingSignerEmailConfirmation.value = true
                return
            }
        }
        signatureRequestOpen.value = true
    }

    const continueStartSignature = () => {
        showMissingSignerEmailConfirmation.value = false
        signatureRequestOpen.value = true
    }

    const closeSignatureRequestDialog = () => {
        signatureRequestOpen.value = false
    }

    const onMissingRowHover = (doc) => {
        hoveredRowData.value = { doc }
    }

    const onRowHover = (bundle, doc) => {
        hoveredRowData.value = { bundle, doc }
    }

    const onRowLeave = () => {
        hoveredRowData.value = null
    }

    const isRowHovered = (bundle, doc) => {
        if (!hoveredRowData.value) return false
        if (hoveredRowData.value.bundle && hoveredRowData.value.doc) {
            return hoveredRowData.value.bundle.id === bundle.id && hoveredRowData.value.doc.id === doc.id
        }
        if (hoveredRowData.value.doc && !hoveredRowData.value.bundle) {
            return hoveredRowData.value.doc.id === doc.id
        }
        return false
    }

    const onRowClick = (bundle, doc) => {
        if (isRowPinned.value && pinnedRowData.value?.bundle?.id === bundle.id && pinnedRowData.value?.doc?.id === doc.id) {
            pinnedRowData.value = null
            isRowPinned.value = false
            return
        }
        pinnedRowData.value = { bundle, doc }
        isRowPinned.value = true
    }

    const isPinnedBundle = (bundle, rowType) => {
        if (!isRowPinned.value) return false
        const pinnedType = pinnedRowData.value && pinnedRowData.value.bundle ? getBundleRowType(pinnedRowData.value.bundle) : null
        if (pinnedType !== rowType) return false
        if (!pinnedRowData.value) return false
        return pinnedRowData.value?.bundle?.id === bundle?.id
    }

    const isPinnedRow = (bundle, doc, rowType) => {
        if (!isRowPinned.value) return false
        const pinnedType = pinnedRowData.value && pinnedRowData.value.bundle ? getBundleRowType(pinnedRowData.value.bundle) : null
        if (pinnedType !== rowType) return false
        if (!pinnedRowData.value) return false
        return pinnedRowData.value?.bundle?.id === bundle?.id && pinnedRowData.value?.doc?.id === doc?.id
    }

    const displayedRow = computed(() => {
        // If pinned and there's a temporary hover over a different row, show hovered temporarily
        const pinnedType = pinnedRowData.value && pinnedRowData.value.bundle ? getBundleRowType(pinnedRowData.value.bundle) : null
        const hoveredType = (function(d) {
            if (!d) return null
            if (d.bundle) return getBundleRowType(d.bundle)
            if (d.doc && !d.bundle) return 'missing'
            return null
        })(hoveredRowData.value)

        if (isRowPinned.value && hoveredRowData.value) {
            // Check if hovering over a different row (regardless of type)
            const isDifferentRow = !(
                pinnedRowData.value?.bundle?.id === hoveredRowData.value?.bundle?.id &&
                pinnedRowData.value?.doc?.id === hoveredRowData.value?.doc?.id
            )
            if (isDifferentRow) {
                return { data: hoveredRowData.value, type: hoveredType, isPinned: false }
            }
        }
        if (isRowPinned.value) {
            return { data: pinnedRowData.value, type: pinnedType, isPinned: true }
        }
        if (hoveredType) {
            return { data: hoveredRowData.value, type: hoveredType, isPinned: false }
        }
        return { data: null, type: null, isPinned: false }
    })

    const displayedRowServiceDetailSections = computed(() => {
        if (!displayedRow.value || !displayedRow.value.data) return []
        if (displayedRow.value.type === 'missing') return []

        return getBundleServiceDetailSummary(displayedRow.value.data.bundle, {
            sortDocuments: sortDocumentsByTagConfigOrder,
            resolveDocumentTitle: (doc) => documentTemplatesMap.value[doc?.document_template_id]?.name || doc?.document_template_id || 'Document',
            resolveContactName: getContactName,
        })
    })

    const isSignatureBundleSubmitting = ref(false)
    const isCanceling = ref(false)
    const isDeleting = ref(false)

    async function deleteDocument(doc, bundle) {
        documentToDelete.value = doc
        bundleToDelete.value = bundle
        confirmingDocumentDeletion.value = true
    }

    async function cancelSigningRequest(bundle) {
        bundleToCancel.value = bundle
        confirmingCancelSigning.value = true
    }

    async function confirmCancelSigningRequest() {
        const bundle = bundleToCancel.value
        confirmingCancelSigning.value = false
        bundleToCancel.value = null
        if (!client.value || !client.value.id || !bundle || !bundle.id) return
        if (isCanceling.value) return
        isCanceling.value = true
        try {
            const resp = await clientDocumentStore.cancel(client.value.id, bundle.id)
            notificationStore.add('Signing request canceled')
            try {
                const updated = resp && resp.data ? resp.data : resp
                if (updated) {
                    // try to merge update into client bundle list
                    const bundlesArr = client.value.document_bundles || []
                    const idx = bundlesArr.findIndex(b => b.id === updated.id)
                    if (idx !== -1) {
                        bundlesArr.splice(idx, 1, updated)
                    }
                }
            } catch (e) {
                // ignore update errors
            }
        } catch (err) {
            const details = err?.response?.data?.message || err?.message || 'Unknown error'
            notificationStore.add('Cancel failed', 'error', details)
        } finally {
            isCanceling.value = false
        }
    }

    async function confirmDeleteDocument() {
        const bundle = bundleToDelete.value
        confirmingDocumentDeletion.value = false
        documentToDelete.value = null
        bundleToDelete.value = null
        if (!client.value || !client.value.id || !bundle || !bundle.id) return
        if (isDeleting.value) return
        isDeleting.value = true
        try {
            const resp = await clientDocumentStore.deleteBundle(client.value.id, bundle.id)
            notificationStore.add('Document deleted')
            try {
                const deleted = resp && resp.data ? resp.data : resp
                if (deleted) {
                    // remove bundle from client list
                    const bundlesArr = client.value.document_bundles || []
                    const idx = bundlesArr.findIndex(b => b.id === bundle.id)
                    if (idx !== -1) {
                        bundlesArr.splice(idx, 1)
                    }
                }
            } catch (e) {
                // ignore update merge errors
            }
        } catch (err) {
            const details = err?.response?.data?.message || err?.message || 'Unknown error'
            notificationStore.add('Delete failed', 'error', details)
        } finally {
            isDeleting.value = false
        }
    }

    async function handleSignatureRequestSubmit(payload) {
        if (!client.value || !client.value.id) return
        isSignatureBundleSubmitting.value = true
        try {
            const resp = await clientDocumentStore.sendForSignature(client.value.id, payload)
            notificationStore.add('Document sent for signature successfully')
            try {
                const bundle = resp && resp.data ? resp.data : resp
                if (bundle) {
                    client.value.document_bundles = client.value.document_bundles || []
                    client.value.document_bundles.push(bundle)
                }
            } catch (e) {
                // ignore
            }
            closeSignatureRequestDialog()
        } catch (err) {
            const details = err?.response?.data?.message || err?.message || 'Unknown error'
            notificationStore.add('Failed to send for signature', 'error', details)
            // keep dialog open for retry
        } finally {
            isSignatureBundleSubmitting.value = false
        }
    }

    const triggerFileSelect = (e) => {
        e && e.preventDefault()
        if (uploadInProgress.value) return
        if (fileInput.value && fileInput.value.click) {
            fileInput.value.click()
        }
    }

    const handleFileSelect = (e) => {
        const files = e.target.files || e.dataTransfer && e.dataTransfer.files
        if (!files || files.length === 0) return
        const file = files[0]
        validateAndUpload(file)
        // clear input
        e.target.value = null
    }

    function focusFirstPendingBundle() {
        if (pendingSignatureBundles.value.length > 0) {
            const firstBundle = pendingSignatureBundles.value[0]
            if (firstBundle && firstBundle.documents && firstBundle.documents.length > 0) {
                const firstDoc = sortDocumentsByTagConfigOrder(firstBundle.documents)[0]
                if (firstDoc) {
                    onRowHover(firstBundle, firstDoc)
                    onRowClick(firstBundle, firstDoc)
                    util.scrollIntoView('clientDocumentsPendingSignatureBundleContainer')
                    // , `#clientDocumentsRow-${firstBundle.id}-${firstDoc.id}`, 100, 100)
                }
            }
        }
    }

    function countDocuments(bundles) {
        if (!bundles || !Array.isArray(bundles)) return 0
        let count = 0
        bundles.forEach(bundle => {
            if (!bundle || !bundle.documents) return
            bundle.documents.forEach(doc => {
                if (doc.tags) {
                    count += doc.tags.length
                } else {
                    count += 1
                }
            })
        })
        return count
    }

    const documentRowCount = computed(() => {
        let rowCount = 0
        if (pendingSignatureBundles.value.length > 0 || missingDocuments.value.length > 0) { rowCount += 1 }
        if (missingDocuments.value) { rowCount += missingDocuments.value.length }
        if (manuallyUploadedBundles.value) { rowCount += countDocuments(manuallyUploadedBundles.value) }
        if (pendingSignatureBundles.value) { rowCount += countDocuments(pendingSignatureBundles.value) }
        if (completedDocumentBundles.value) { rowCount += countDocuments(completedDocumentBundles.value) }
        if (declinedDocumentBundles.value) { rowCount += countDocuments(declinedDocumentBundles.value) }
        if (expiredDocumentBundles.value) { rowCount += countDocuments(expiredDocumentBundles.value) }
        return rowCount
    })

    const showInlineUpload = computed(() => {
        return documentRowCount.value <= 6
    })

    function handleFileDrop(files) {
        if (uploadInProgress.value || !files || files.length === 0) return
        validateAndUpload(files[0])
    }

    const validateAndUpload = (file) => {
        if (!client.value?.id && !client.value?.uuid) {
            return
        }

        const name = file.name || ''
        const extMatch = name.match(/\.([^.]+)$/)
        const ext = extMatch ? extMatch[1].toLowerCase() : ''
        if (!allowedExtensions.includes(ext)) {
            notificationStore.add('Invalid file type', 'error', `Allowed types: ${allowedExtensions.join(', ')}`)
            return
        }
        uploadFile(file)
    }

    const uploadFile = (file) => {
        const preTag = pendingMissingDocTag.value
        pendingMissingDocTag.value = null

        function onUploadProgress(e) {
            const total = e?.total
            if (!total) return
            uploadProgress.value = Math.round((e.loaded / total) * 100)
        }

        uploadInProgress.value = true
        uploadProgress.value = 0

        clientDocumentStore.uploadFile(file, client.value?.uuid, onUploadProgress)
            .then((resp) => {
                notificationStore.add('Document uploaded')
                try {
                    const bundle = resp && resp.data ? resp.data : resp
                    if (bundle) {
                        const wasCreateMode = !client.value.id
                        if (wasCreateMode && bundle?.client_id) {
                            pendingCreateClientId.value = bundle.client_id
                        }
                        client.value.document_bundles = client.value.document_bundles || []
                        client.value.document_bundles.push(bundle)
                        openTagModalForNewUpload(bundle, preTag)
                    }
                } catch (e) {
                    // ignore push failures
                }
            })
            .catch((err) => {
                const details = err?.response?.data?.message || err?.message || 'Unknown error'
                notificationStore.add('Upload failed', 'error', details)
            })
            .finally(() => {
                uploadInProgress.value = false
                uploadProgress.value = 0
            })
    }

    function getCompletedDocumentDisplayDate(bundle) {
        if (bundle.signature_pandadoc_document_status === 'document.completed') {
            return bundle.signature_pandadoc_all_completed_at ? 'Completed ' + moment(bundle.signature_pandadoc_all_completed_at).fromNow() : '—'
        } else if (bundle.signature_pandadoc_document_status === 'document.declined') {
            return bundle.signature_pandadoc_declined_at ? 'Declined ' + moment(bundle.signature_pandadoc_declined_at).fromNow() : '—'
        } else {
            return '—'
        }
    }

    function getPendingSignatureStatusDisplay(bundle) {
        if (!bundle.signature_pandadoc_document_status) {
            return 'Creation & Sending in Progress'
        }

        switch (bundle.signature_pandadoc_document_status) {
            case 'document.uploaded':
                return 'Creation & Sending in Progress'
            case 'document.draft':
                return 'Creation & Sending in Progress'
            case 'document.sent':
                return 'Sent'
            case 'document.viewed':
                return 'Viewed'
            default:
                return bundle.signature_pandadoc_document_status
        }
    }

    function getSignatureProgress(doc) {
        if (!doc || !doc.signature_signer_contact_users || doc.signature_signer_contact_users.length === 0) return 0

        const signed = doc.signature_signer_contact_users.filter(s => s.signature_pandadoc_completed_at).length

        return `${signed}/${doc.signature_signer_contact_users.length}`
    }

    function openTagModalForNewUpload(bundle, preTag = null) {
        const firstDoc = bundle?.documents?.[0]
        if (!firstDoc) return
        openTagModal(firstDoc, bundle, preTag ? [preTag] : [])
    }

    function downloadFileName(doc) {
        let filename = ''

        if (client.value) {
            const lastName = client.value.last_name || ''
            const firstName = client.value.first_name || ''

            if (lastName && firstName) {
                filename += `${lastName}, ${firstName}`
            } else if (lastName) {
                filename += lastName
            } else if (firstName) {
                filename += firstName
            }

            if (filename) {
                filename += ' - '
            }
        }

        if (!doc) return filename + 'document'

        const templateName = documentTemplatesMap.value[doc.document_template_id]?.name || ''
        if (templateName) {
            return filename + templateName
        }

        // Handle manually uploaded documents with tags
        if (doc.tags && Array.isArray(doc.tags) && doc.tags.length > 0) {
            const sortedTags = sortTagsByConfigOrder(doc.tags)

            if (sortedTags.length === 1) {
                return filename + sortedTags[0]
            } else if (sortedTags.length === 2) {
                return filename + sortedTags[0] + ' and ' + sortedTags[1]
            } else {
                // 3+ tags: use first tag and "and Additional Onboarding Documents"
                return filename + sortedTags[0] + ' and Additional Onboarding Documents'
            }
        }

        if (doc.manual_upload_original_filename) {
            return filename + doc.manual_upload_original_filename
        }

        return filename + 'document'
    }

    const handleDownloadDocument = async (doc) => {
        try {
            const filename = downloadFileName(doc)
            await util.downloadFile(doc.download_url, filename)
        } catch (error) {
            notificationStore.add('Download Failed', 'error', error.message)
        }
    }

    function triggerMissingDocUpload(e, doc) {
        e.stopPropagation()
        if (uploadInProgress.value) return
        pendingMissingDocTag.value = doc.name
        if (missingDocFileInput.value) {
            missingDocFileInput.value.click()
        }
    }

    function setBelowModuleDropZoneEl(el) {
        belowModuleDropZoneEl.value = el
    }

    const handleMissingDocFileSelect = (e) => {
        const files = e.target.files
        if (!files || files.length === 0) {
            pendingMissingDocTag.value = null
            return
        }
        const file = files[0]
        validateAndUpload(file)
        e.target.value = null
    }

    provide('clientDocsUpload', {
        isOverBelowDropZone,
        uploadInProgress,
        uploadProgress,
        triggerFileSelect,
        belowModuleDropZoneEl,
    })

    defineExpose({
        showInlineUpload,
        isOverBelowDropZone,
        uploadInProgress,
        uploadProgress,
        missingDocuments,
        pendingSignatureBundles,
        pendingDocumentsCount,
        documentStatus,
        triggerFileSelect,
        focusFirstPendingBundle,
        openSignatureRequestDialog,
        closeSignatureRequestDialog,
        signatureRequestOpen,
        setBelowModuleDropZoneEl,
        startDocumentsTour,
    })

    function getBundleRowType(bundle) {
        if (!bundle) return null
        if (!bundle.uses_signature_workflow) return 'uploaded'
        if (bundle.signature_pandadoc_document_status === 'document.completed') return 'completed'
        if (bundle.signature_pandadoc_document_status === 'document.declined') return 'declined'
        if (expiredStatuses.includes(bundle.signature_pandadoc_document_status) || bundle.signature_canceled_at) return 'expired'
        return 'pending'
    }

    function sortTagsByConfigOrder(tags) {
        if (!tags || !Array.isArray(tags) || tags.length === 0) return []

        const configTagOrder = documentTags.value.map(t => t.id || t.name || t)

        return [...tags].sort((a, b) => {
            const aIndex = configTagOrder.indexOf(a)
            const bIndex = configTagOrder.indexOf(b)

            if (aIndex === -1 && bIndex === -1) return 0
            if (aIndex === -1) return 1
            if (bIndex === -1) return -1

            return aIndex - bIndex
        })
    }

    function sortDocumentsByTagConfigOrder(documents) {
        if (!documents || !Array.isArray(documents)) return []

        const configTagOrder = documentTags.value.map(t => t.id || t.name || t)

        // Precompute min tag index for each document once
        const docsWithIndices = documents.map(doc => {
            let minIndex = Infinity
            if (doc && doc.tags && Array.isArray(doc.tags) && doc.tags.length > 0) {
                minIndex = Math.min(...doc.tags.map(tag => {
                    const idx = configTagOrder.indexOf(tag)
                    return idx === -1 ? Infinity : idx
                }))
            }
            return { doc, minIndex }
        })

        return docsWithIndices.sort((a, b) => {
            if (a.minIndex === Infinity && b.minIndex === Infinity) return 0
            if (a.minIndex === Infinity) return 1
            if (b.minIndex === Infinity) return -1
            return a.minIndex - b.minIndex
        }).map(item => item.doc)
    }

    function getContactName(contactUserId) {
        if (!client.value || !client.value.contacts) return 'Unknown Contact'
        const contact = client.value.contacts.find(c => c.id === contactUserId)
        return contact ? `${contact.first_name} ${contact.last_name}` : 'Unknown Contact'
    }

    function getAgencyAndSignerDisplay() {
        if (!displayedRow.value || !displayedRow.value.data) return ''
        const doc = displayedRow.value.data.doc
        const bundle = displayedRow.value.data.bundle

        if (!doc.primary_signer || !doc.agency_name) return ''

        let display = `${doc.primary_signer.name} for ${doc.agency_name}`

        // Add creator staff if different from primary signer
        if (bundle && bundle.creator_staff) {
            const creatorName = `${bundle.creator_staff.first_name} ${bundle.creator_staff.last_name}`.trim()
            const primaryName = doc.primary_signer.name
            if (creatorName && creatorName !== primaryName) {
                display += ` (prepared by ${creatorName})`
            }
        }

        return display
    }

    function canPreviewFile(doc) {
        if (!doc) return false
        if (doc.preview_url) {
            return true
        }
        if (doc.manual_upload_original_filename) {
            const extMatch = doc.manual_upload_original_filename.match(/\.([^.]+)$/)
            const ext = extMatch ? extMatch[1].toLowerCase() : ''
            return previewableExtensions.includes(ext)
        }
        return false
    }

    function canDownloadFile(doc) {
        if (!doc) return false
        return !!doc.download_url
    }

    function handleMissingEmailConfirmationClose() {
        showMissingSignerEmailConfirmation.value = false
        emit('missing-primary-email-confirmation-closed', firstMissingSignerEmailContactId.value)
        firstMissingSignerEmailContactId.value = null
    }

</script>

<template>

    <div class="relative">

        <!-- Hidden file inputs -->
        <input ref="fileInput" type="file" class="hidden" @change="handleFileSelect" />
        <input ref="missingDocFileInput" type="file" class="hidden" @change="handleMissingDocFileSelect" />

                <!-- Main module container -->
                <div class="relative bg-white flex flex-col h-[32rem]">
            <div v-if="!hasClientData && loading" class="py-36 bg-zinc-100 flex-1">
                <Loader />
            </div>
            <template v-else>

                <!-- Missing Documents Header -->
                <template v-if="missingDocuments && missingDocuments.length > 0">
                    <div class="flex-shrink-0 bg-lifeworx-red-500 flex items-center justify-between px-4 py-2 text-white">
                        <div class="flex items-center space-x-4">
                            <IconDocumentEdit16Regular class="size-6 flex-shrink-0" />
                            <h3 class="text-sm font-bold">Missing Required Client Documents</h3>
                        </div>
                        <button
                            type="button"
                            @click="openSignatureRequestDialog()"
                            class="flex items-center space-x-2 bg-lifeworx-red-500 text-white px-5 py-2.5 font-semibold text-sm hover:bg-lifeworx-red-600 transition flex-shrink-0"
                        >
                            <span>Create Signature Request</span>
                            <div class="flex items-center justify-center size-5 bg-white rounded-full">
                                <ArrowRightIcon class="size-3 text-lifeworx-red-500" />
                            </div>
                        </button>
                    </div>
                </template>

                <!-- Pending Documents Header -->
                <template v-else-if="pendingSignatureBundles && pendingSignatureBundles.length > 0 || pendingDocumentsCount && pendingDocumentsCount > 0">
                    <div class="flex-shrink-0 bg-lifeworx-orange-500 flex items-center justify-between px-4 py-2 text-white">
                        <div class="flex items-center space-x-4">
                            <IconDocumentError16Filled class="size-6 flex-shrink-0" />
                            <h3 class="text-sm font-bold">{{ pendingDocumentsCount }} {{ pendingDocumentsCount === 1 ? 'Document' : 'Documents' }} Pending Completion</h3>
                        </div>
                        <button
                            type="button"
                            @click="focusFirstPendingBundle()"
                            class="flex items-center space-x-2 bg-lifeworx-orange-500 text-white px-5 py-2.5 font-semibold text-sm hover:bg-lifeworx-orange-600 transition flex-shrink-0"
                        >
                            <span>View</span>
                            <div class="flex items-center justify-center size-5 bg-white rounded-full">
                                <ArrowRightIcon class="size-3 text-lifeworx-red-500" />
                            </div>
                        </button>
                    </div>
                </template>

                <!-- Document groups + detail panel -->
                <div class="grid grid-cols-3 flex-1 min-h-0">

                    <!-- Documents List -->
                    <div
                        id="client-profile-documents-module-list-area"
                        ref="documentsListAreaRef"
                        class="col-span-2 border-r border-stone-200 overflow-y-auto min-h-full flex flex-col"
                    >

                        <div class="px-4 flex-1">

                            <!-- Missing Documents -->
                            <div class="mt-4">
                                <template v-if="missingDocuments && missingDocuments.length > 0">
                                    <div class="border rounded-lg border-lifeworx-red-50 hover:border-lifeworx-red-600/20 overflow-clip">
                                        <div
                                            v-for="doc in missingDocuments"
                                            :key="doc.id"
                                            class="group flex items-center px-4 py-2 bg-lifeworx-red-300 bg-opacity-5 odd:bg-opacity-10 hover:bg-opacity-20 font-semibold text-sm text-lifeworx-red-500 cursor-pointer"
                                            @mouseenter="onMissingRowHover(doc)"
                                            @mouseleave="onRowLeave()"
                                            @click="openSignatureRequestDialog()"
                                        >
                                            <div class="w-[58%] min-w-0 flex items-center gap-2">
                                                <ExclamationTriangleIcon class="size-4 flex-shrink-0" />
                                                <span class=" truncate select-none">{{ doc.name }}</span>
                                            </div>
                                            <div class="w-[32%] flex-shrink-0 text-xs font-bold text-lifeworx-red-500 text-right">Missing</div>
                                            <div class="w-[15%] flex-shrink-0 flex items-center justify-end gap-0.5 opacity-25 group-hover:opacity-75 hover:opacity-100">
                                                <button
                                                    type="button"
                                                    class="p-1 hover:text-lifeworx-red-700 hover:opacity-100 "
                                                    title="Upload document"
                                                    @click.stop="triggerMissingDocUpload($event, doc)"
                                                >
                                                    <IconDocumentArrowUp16Regular class="size-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    class="p-1 hover:text-lifeworx-red-700 hover:opacity-100 "
                                                    title="Send for signature"
                                                    @click.stop="openSignatureRequestDialog()"
                                                >
                                                    <IconSignature16Regular class="size-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>

                            <!-- Pending Signature Bundles -->
                            <div v-if="pendingSignatureBundles && pendingSignatureBundles.length > 0" class="mt-4" id="clientDocumentsPendingSignatureBundleContainer">
                                <h4 v-if="missingDocuments && missingDocuments.length > 0" class="text-xs font-semibold text-stone-500 mb-2">Pending</h4>
                                <div class="flex flex-col gap-2">
                                    <div
                                        v-for="bundle in pendingSignatureBundles"
                                        :key="bundle.id"
                                    >
                                        <div :class="['px-0 border rounded-lg border-lifeworx-orange-200 cursor-pointer', { 'ring-2 ring-lifeworx-orange-400 ring-inset': isPinnedBundle(bundle, 'pending') }]">
                                            <div v-for="doc in sortDocumentsByTagConfigOrder(bundle.documents)" :key="doc.id"
                                                :class="[
                                                    'flex items-center rounded-lg px-4 py-2 text-lifeworx-orange-500 font-semibold text-sm cursor-pointer',
                                                    { 'bg-lifeworx-orange-300 bg-opacity-5 odd:bg-opacity-15 hover:bg-opacity-25' : !isPinnedRow(bundle, doc, 'pending') },
                                                    { 'bg-lifeworx-orange-300 bg-opacity-50': isPinnedRow(bundle, doc, 'pending') },
                                                ]"
                                                @mouseenter="onRowHover(bundle, doc)"
                                                @mouseleave="onRowLeave()"
                                                @click="onRowClick(bundle, doc)"
                                            >
                                                <div class="w-[58%] min-w-0 flex items-center gap-2">
                                                    <IconDocumentTextClock20Regular class="size-4 flex-shrink-0" />
                                                    <div class="truncate select-none">{{ documentTemplatesMap[doc.document_template_id]?.name}}</div>
                                                    <div class="flex-shrink-0 flex ml-1 items-center gap-1">
                                                        <IconSignature16Regular class="size-4 flex-shrink-0" /><span class="text-xs">{{ getSignatureProgress(doc) }}</span>
                                                    </div>
                                                </div>
                                                <div
                                                    class="w-[32%] flex-shrink-0 text-right text-xs whitespace-nowrap truncate"
                                                    :title="bundle.signature_pandadoc_sent_at ? moment(bundle.signature_pandadoc_sent_at).utc().format('MMMM Do YYYY, h:mm:ss a') : null"
                                                >
                                                    {{ bundle.signature_pandadoc_sent_at ? 'Sent ' + moment(bundle.signature_pandadoc_sent_at).fromNow() : getPendingSignatureStatusDisplay(bundle) }}
                                                </div>
                                                <div class="w-[15%] flex-shrink-0"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Completed Documents -->
                            <div v-if="completedDocumentBundles && completedDocumentBundles.length > 0" class="mt-4">
                                <h4 class="text-xs font-semibold text-stone-500 mb-2">Completed</h4>
                                <div class="gap-2 flex flex-col">
                                    <div v-for="bundle in completedDocumentBundles" :key="bundle.id" :class="[
                                        'border rounded-lg overflow-clip border-lifeworx-green-300 cursor-pointer',
                                        { 'ring-2 ring-lifeworx-green-400 ring-inset': isPinnedBundle(bundle, 'completed') }
                                    ]">
                                        <div v-for="doc in sortDocumentsByTagConfigOrder(bundle.documents)" :key="doc.id"
                                            :class="[
                                                'flex items-center px-4 py-2 text-lifeworx-green-500 transition text-sm font-semibold cursor-pointer group',
                                                { 'bg-lifeworx-green-300 bg-opacity-5 odd:bg-opacity-15 hover:bg-opacity-25' : !isPinnedRow(bundle, doc, 'completed') },
                                                { 'bg-lifeworx-green-300 bg-opacity-50': isPinnedRow(bundle, doc, 'completed') }
                                            ]"
                                            @mouseenter="onRowHover(bundle, doc)"
                                            @mouseleave="onRowLeave()"
                                            @click="onRowClick(bundle, doc)"
                                        >
                                            <div class="w-[58%] min-w-0 flex items-center gap-2">
                                                <IconDocumentEdit16Regular class="size-4 flex-shrink-0" />
                                                <div class="truncate select-none">{{ documentTemplatesMap[doc.document_template_id]?.name || doc.document_template_id || 'Document' }}</div>
                                                <div class="flex-shrink-0 ml-1 flex items-center gap-1 select-none">
                                                    <IconSignature16Regular class="size-4 flex-shrink-0" /><span class="text-xs">{{ getSignatureProgress(doc) }}</span>
                                                </div>
                                            </div>
                                            <div
                                                class="w-[32%] flex-shrink-0 text-right text-xs select-none whitespace-nowrap truncate">{{ getCompletedDocumentDisplayDate(bundle) }}</div>
                                            <div class="w-[15%] flex-shrink-0 flex items-center justify-end gap-0.5 opacity-25 group-hover:opacity-75 hover:opacity-100 transition-opacity duration-150">
                                                <button
                                                    v-if="canPreviewFile(doc)"
                                                    type="button"
                                                    @click.prevent="handlePreviewDocument(doc)"
                                                    class="p-1 opacity-50 hover:opacity-100 text-lifeworx-green-800"
                                                ><EyeIcon class="size-4"/></button>
                                                <button
                                                    v-if="canDownloadFile(doc)"
                                                    type="button"
                                                    @click.prevent="handleDownloadDocument(doc)"
                                                    class="p-1 opacity-50 hover:opacity-100 text-lifeworx-green-800"
                                                ><ArrowDownTrayIcon class="size-4"/></button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Manually Uploaded Bundles -->
                            <div v-if="manuallyUploadedBundles && manuallyUploadedBundles.length > 0" class="mt-4">
                                <h4 class="text-xs font-semibold text-stone-500 mb-2">Manually Uploaded</h4>
                                <div class="flex flex-col gap-2">
                                    <template
                                        v-for="bundle in manuallyUploadedBundles"
                                        :key="bundle.id"
                                    >
                                        <template
                                            v-for="doc in bundle.documents"
                                            :key="doc.id"
                                        >
                                            <div
                                                :class="[
                                                    'flex items-center px-4 py-2 group font-semibold text-sm cursor-pointer bg-lifeworx-orange-400 bg-opacity-5 odd:bg-opacity-15 hover:bg-opacity-25 text-lifeworx-orange-600 border rounded-lg border-lifeworx-orange-300',
                                                    { 'ring-2 ring-lifeworx-orange-400 ring-inset': isPinnedRow(bundle, doc, 'uploaded') }
                                                ]"
                                                v-if="doc.tags && doc.tags.length == 0"
                                                @mouseenter="onRowHover(bundle, doc)"
                                                @mouseleave="onRowLeave()"
                                                @click="onRowClick(bundle, doc)"
                                            >
                                                <div class="w-[58%] min-w-0 flex items-center gap-2">
                                                    <IconQuestionCircle12Filled class="size-4 flex-shrink-0" />
                                                    <div class="truncate text-sm select-none">{{ isRowHovered(bundle, doc) ? doc.manual_upload_original_filename : 'Unknown Contents' }}</div>

                                                </div>
                                                <div
                                                    class="w-[32%] flex-shrink-0 text-right text-xs whitespace-nowrap select-none truncate"
                                                    :title="moment(doc.created_at).utc().format('MMMM Do YYYY, h:mm:ss a')"
                                                >Uploaded {{ moment(doc.created_at).fromNow() }}</div>
                                                <div class="w-[15%] flex-shrink-0 flex items-center justify-end gap-0.5 opacity-25 group-hover:opacity-75 hover:opacity-100 transition-opacity duration-150">
                                                    <button
                                                        v-if="canPreviewFile(doc)"
                                                        type="button"
                                                        @click.prevent="handlePreviewDocument(doc)"
                                                        class="p-1 opacity-50 hover:opacity-100"
                                                    ><EyeIcon class="size-4"/></button>
                                                    <button
                                                        v-if="canDownloadFile(doc)"
                                                        type="button"
                                                        @click.prevent="handleDownloadDocument(doc)"
                                                        class="p-1 opacity-50 hover:opacity-100"
                                                    ><ArrowDownTrayIcon class="size-4"/></button>
                                                    <button
                                                        type="button"
                                                        @click.prevent="openTagModal(doc, bundle)"
                                                        class="p-1 opacity-50 hover:opacity-100"
                                                    ><TagIcon class="size-4"/></button>
                                                </div>
                                            </div>
                                            <div v-else
                                                :class="['border rounded-lg border-lifeworx-green-300', { 'ring-2 ring-lifeworx-green-400 ring-inset': isPinnedRow(bundle, doc, 'uploaded') }]"
                                                @mouseenter="onRowHover(bundle, doc)"
                                                @mouseleave="onRowLeave()"
                                                @click="onRowClick(bundle, doc)"
                                            >
                                                <template
                                                    v-for="tag in sortTagsByConfigOrder(doc.tags)"
                                                    :key="tag"
                                                >
                                                    <div class="flex items-center px-4 py-2 group font-semibold text-sm cursor-pointer bg-lifeworx-green-300 bg-opacity-5 odd:bg-opacity-10 hover:bg-opacity-25 text-lifeworx-green-600">
                                                        <div class="w-[58%] min-w-0 flex items-center gap-2">
                                                            <IconDocumentArrowUp16Regular class="size-4 flex-shrink-0" />
                                                            <div class="truncate select-none">{{ tag }}</div>
                                                        </div>
                                                        <div
                                                            class="w-[32%] flex-shrink-0 text-right text-xs whitespace-nowrap truncate"
                                                            :title="moment(doc.created_at).utc().format('MMMM Do YYYY, h:mm:ss a')"
                                                        >Uploaded {{ moment(doc.created_at).fromNow() }}</div>
                                                        <div class="w-[15%] flex-shrink-0 flex items-center justify-end gap-0.5 opacity-25 group-hover:opacity-75 hover:opacity-100 transition-opacity duration-150">
                                                            <button
                                                                v-if="canPreviewFile(doc)"
                                                                type="button"
                                                                @click.prevent="handlePreviewDocument(doc)"
                                                                class="p-1 opacity-50 hover:opacity-100 text-lifeworx-green-800"
                                                            ><EyeIcon class="size-4"/></button>
                                                            <button
                                                                v-if="canDownloadFile(doc)"
                                                                type="button"
                                                                @click.prevent="handleDownloadDocument(doc)"
                                                                class="p-1 opacity-50 hover:opacity-100 text-lifeworx-green-800"
                                                            ><ArrowDownTrayIcon class="size-4"/></button>
                                                            <button
                                                                type="button"
                                                                @click.prevent="openTagModal(doc, bundle)"
                                                                class="p-1 opacity-50 hover:opacity-100 text-lifeworx-green-800"
                                                            ><TagIcon class="size-4"/></button>
                                                        </div>
                                                    </div>
                                                </template>
                                            </div>
                                        </template>
                                    </template>
                                </div>
                            </div>

                            <!-- Declined -->
                            <div v-if="declinedDocumentBundles && declinedDocumentBundles.length > 0" class="mt-4">
                                <h4 class="text-xs font-semibold text-stone-500 mb-2">Declined</h4>
                                <div class="gap-2 flex flex-col">
                                    <div v-for="bundle in declinedDocumentBundles" :key="bundle.id" :class="[
                                        'border rounded-lg overflow-clip border-lifeworx-red-200 cursor-pointer',
                                        { 'ring-2 ring-lifeworx-red-400 ring-inset': isPinnedBundle(bundle, 'declined') }
                                    ]">
                                        <div v-for="doc in sortDocumentsByTagConfigOrder(bundle.documents)" :key="doc.id"
                                            :class="[
                                                'flex items-center px-4 py-2 text-lifeworx-red-500 transition text-sm font-semibold cursor-pointer group',
                                                { 'bg-lifeworx-red-300 bg-opacity-5 odd:bg-opacity-15 hover:bg-opacity-25' : !isPinnedRow(bundle, doc, 'declined') },
                                                { 'bg-lifeworx-red-300 bg-opacity-50': isPinnedRow(bundle, doc, 'declined') }
                                            ]"
                                            @mouseenter="onRowHover(bundle, doc)"
                                            @mouseleave="onRowLeave()"
                                            @click="onRowClick(bundle, doc)"
                                        >
                                            <div class="w-[58%] min-w-0 flex items-center gap-2">
                                                <XCircleIcon class="size-4 flex-shrink-0" />
                                                <div class="truncate select-none">{{ documentTemplatesMap[doc.document_template_id]?.name || doc.document_template_id || 'Document' }}</div>
                                                <div class="flex-shrink-0 flex ml-1 items-center gap-1 select-none">
                                                    <IconSignature16Regular class="size-4 flex-shrink-0" /><span class="text-xs">{{ getSignatureProgress(doc) }}</span>
                                                </div>
                                            </div>
                                            <div class="w-[32%] flex-shrink-0 text-right text-xs select-none whitespace-nowrap truncate">{{ getCompletedDocumentDisplayDate(bundle) }}</div>
                                            <div class="w-[15%] flex-shrink-0 flex items-center justify-end gap-0.5 opacity-25 group-hover:opacity-75 hover:opacity-100 transition-opacity duration-150">
                                                <button
                                                    v-if="canPreviewFile(doc)"
                                                    type="button"
                                                    @click.prevent="handlePreviewDocument(doc)"
                                                    class="p-1 opacity-50 hover:opacity-100 text-lifeworx-red-800"
                                                ><EyeIcon class="size-4"/></button>
                                                <button
                                                    v-if="canDownloadFile(doc)"
                                                    type="button"
                                                    @click.prevent="handleDownloadDocument(doc)"
                                                    class="p-1 opacity-50 hover:opacity-100 text-lifeworx-red-800"
                                                ><ArrowDownTrayIcon class="size-4"/></button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Expired -->
                            <div v-if="expiredDocumentBundles && expiredDocumentBundles.length > 0" class="mt-4">
                                <h4 class="text-xs font-semibold text-stone-500 mb-2">Expired / Canceled</h4>
                                <div class="gap-2 flex flex-col">
                                    <div v-for="bundle in expiredDocumentBundles" :key="bundle.id" :class="[
                                        'border rounded-lg overflow-clip border-stone-300 cursor-pointer',
                                        { 'ring-2 ring-stone-400 ring-inset': isPinnedBundle(bundle, 'expired') }
                                    ]">
                                        <div v-for="doc in sortDocumentsByTagConfigOrder(bundle.documents)" :key="doc.id"
                                            :class="[
                                                'flex items-center px-4 py-2 text-stone-500 transition text-sm font-semibold cursor-pointer',
                                                { 'bg-stone-300 bg-opacity-5 odd:bg-opacity-15 hover:bg-opacity-25' : !isPinnedRow(bundle, doc, 'expired') },
                                                { 'bg-stone-300 bg-opacity-50': isPinnedRow(bundle, doc, 'expired') }
                                            ]"
                                            @mouseenter="onRowHover(bundle, doc)"
                                            @mouseleave="onRowLeave()"
                                            @click="onRowClick(bundle, doc)"
                                        >
                                            <div class="w-[58%] min-w-0 flex items-center gap-2">
                                                <IconDocumentTextClock20Regular class="size-4 flex-shrink-0" />
                                                <div class="truncate select-none">{{ documentTemplatesMap[doc.document_template_id]?.name || doc.document_template_id || 'Document' }}</div>
                                                <div class="ml-1 flex-shrink-0 flex items-center gap-1">
                                                    <IconSignature16Regular class="size-4 flex-shrink-0" /><span class="text-xs select-none">{{ getSignatureProgress(doc) }}</span>
                                                </div>
                                            </div>
                                            <div
                                                class="w-[32%] flex-shrink-0 text-right text-xs select-none whitespace-nowrap truncate"
                                                :title="moment(bundle.signature_pandadoc_expires_at).utc().format('MMMM Do YYYY, h:mm:ss a')"
                                            >
                                                <template v-if="!bundle.signature_canceled_at">
                                                    Expired {{ bundle.signature_pandadoc_expires_at ? moment(bundle.signature_pandadoc_expires_at).fromNow() : '' }}
                                                </template>
                                                <template v-else>
                                                    Canceled {{ bundle.signature_canceled_at ? moment(bundle.signature_canceled_at).fromNow() : '' }}
                                                </template>
                                            </div>
                                            <div class="w-[15%] flex-shrink-0 flex items-center justify-end gap-0.5 opacity-25 group-hover:opacity-75 hover:opacity-100 transition-opacity duration-150">
                                                <button
                                                    v-if="canPreviewFile(doc)"
                                                    type="button"
                                                    @click.prevent="handlePreviewDocument(doc)"
                                                    class="p-1 opacity-50 hover:opacity-100 text-lifeworx-red-800"
                                                ><EyeIcon class="size-4"/></button>
                                                <button
                                                    v-if="canDownloadFile(doc)"
                                                    type="button"
                                                    @click.prevent="handleDownloadDocument(doc)"
                                                    class="p-1 opacity-50 hover:opacity-100 text-lifeworx-red-800"
                                                ><ArrowDownTrayIcon class="size-4"/></button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="pb-4"></div>
                        </div>

                        <!-- Inline upload area -->
                        <div v-show="showInlineUpload" class="bg-white p-4 justify-end">
                            <div
                                ref="inlineDropZoneEl"
                                data-test="upload-area"
                                @click="uploadInProgress || triggerFileSelect()"
                                :class="[
                                    'group rounded-xl border border-dashed px-8 transition-colors ',
                                    isOverInlineDropZone ? 'border-lifeworx-blue-600 bg-lifeworx-blue-50' : 'border-lifeworx-blue-500 hover:bg-stone-50',
                                    uploadInProgress ? 'cursor-wait' : 'cursor-pointer'
                                ]"
                            >
                                <div
                                    v-if="isOverInlineDropZone"
                                    class="flex items-center justify-center gap-2 min-h-[60px] py-3 text-lifeworx-blue-600 font-semibold text-sm"
                                >
                                    <IconDocumentArrowUp16Regular class="size-6" />
                                    <span>Release to upload</span>
                                </div>
                                <div
                                    v-else-if="!uploadInProgress"

                                    :class="[
                                        'flex text-center justify-center h-14  py-3 flex-row items-center gap-1'
                                    ]"
                                >
                                    <div class="flex justify-center">
                                        <div class="cursor-pointer">
                                            <IconDocumentArrowUp16Regular class="size-8 text-lifeworx-blue-600" />
                                        </div>
                                    </div>
                                    <div :class="[
                                        'flex',
                                        {
                                            'flex-row items-center gap-1' : hasAnyDocuments,
                                            'flex-col gap-1' : !hasAnyDocuments
                                        }
                                    ]">
                                        <p class="text-sm leading-tight text-stone-500">
                                            <button class="font-semibold text-lifeworx-blue-600 group-hover:underline">
                                                Upload a file
                                            </button>
                                            <span class="font-normal text-sm leading-tight text-stone-500"> or drag-and-drop it here</span>
                                        </p>
                                    </div>
                                </div>
                                <div class="w-full px-8 py-6" v-if="uploadInProgress">
                                    <ProgressBar :progress="uploadProgress" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Detail area -->
                    <div
                        id="client-profile-documents-module-detail-area"
                        ref="documentsDetailAreaRef"
                        :class="[
                            'col-span-1 bg-stone-200 flex flex-col overflow-y-scroll',
                            {
                                'mb-20' : displayedRow.type && displayedRow.type !== 'missing'
                            }
                        ]"
                    >
                        <div v-if="displayedRow.type === 'missing'" class="flex flex-col items-center justify-center flex-1">
                            <span class="text-lifeworx-red-500 font-semibold italic text-xs px-6 py-4">Click to start a new request</span>
                        </div>

                        <template v-else-if="displayedRow.type && displayedRow.type !== 'missing'">
                            <div class="flex-1 p-6">
                                <div class="flex flex-col gap-5 text-sm">

                                    <!-- Document Tags/Contents -->
                                    <div>
                                        <div class="flex gap-3 items-center ">
                                            <span class="text-stone-500 text-sm font-semibold mb-1">Document Bundle Contents</span>
                                            <button
                                                v-if="displayedRow.type === 'uploaded'"
                                                type="button"
                                                @click.prevent="openTagModal(displayedRow.data.doc, displayedRow.data.bundle)"
                                                class="flex gap-1 justify-center items-center text-xs text-lifeworx-blue-600 hover:underline font-semibold -mt-1"
                                            >
                                                <TagIcon class="size-4 inline" />
                                                Edit Tags
                                            </button>
                                        </div>


                                        <div class="space-y-1 text-lifeworx-blue-900 font-semibold text-xs">
                                            <template v-for="doc in displayedRow.data.bundle.documents" :key="doc.id">
                                                <div v-for="tag in sortTagsByConfigOrder(doc.tags)" :key="tag">
                                                    &bull; {{ tag }}
                                                </div>
                                                <div v-if="doc.tags.length == 0" class="text-xs text-stone-500 italic">
                                                    Unknown
                                                </div>
                                            </template>

                                        </div>
                                    </div>

                                    <!-- Agency Information with Signer/Preparer -->
                                    <template v-if="displayedRow.data.bundle.uses_signature_workflow && displayedRow.data.doc.agency_name && displayedRow.data.doc.primary_signer">
                                        <div class="">
                                            <p class="font-semibold text-xs text-stone-500 mb-1">Agency Signer &amp; Preparer</p>
                                            <p class="text-lifeworx-blue-900 font-semibold text-xs">{{ getAgencyAndSignerDisplay() }}</p>
                                        </div>
                                    </template>

                                    <!-- Uploader Info (for manually uploaded documents) -->
                                    <template v-if="displayedRow.data.doc.manual_upload_original_filename">
                                        <div class="">
                                            <p class="font-semibold text-stone-600 mb-1">Original File Name</p>
                                            <p class="text-stone-700">{{ displayedRow.data.doc.manual_upload_original_filename || 'Unknown' }}</p>
                                        </div>
                                    </template>

                                    <!-- Service Details (rates, etc.) -->
                                    <template v-if="displayedRowServiceDetailSections.length > 0">
                                        <div class="">
                                            <p class="font-semibold text-stone-600 text-xs mb-1">Details</p>
                                            <div class="space-y-3">
                                                <div v-for="section in displayedRowServiceDetailSections" :key="section.id" class="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                                                    <p class="font-semibold text-stone-600 text-xs mb-2">{{ section.title }}</p>
                                                    <div class="space-y-1 text-lifeworx-blue-900 font-semibold text-xs">
                                                        <p v-for="field in section.fields" :key="field.key"><span class="font-semibold">{{ field.label }}:</span> {{ field.value }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>

                                    <!-- Client Contacts & Signing Roles -->
                                    <div v-if="displayedRow.data.doc.signature_signer_contact_users && displayedRow.data.doc.signature_signer_contact_users.length > 0" class="">
                                        <p class="font-semibold text-stone-600 text-xs mb-1">Signers</p>
                                        <div class="space-y-2 text-lifeworx-blue-900 font-semibold text-xs">
                                            <div v-for="signer in displayedRow.data.doc.signature_signer_contact_users" :key="signer.user_id" class="text-xs flex gap-1">
                                                <p>{{ getContactName(signer.user_id) }}</p>
                                                <template v-if="signer.signature_pandadoc_completed_at">
                                                    <span class="text-stone-500" :title="moment(signer.signature_pandadoc_completed_at).utc().format('MMM D, YYYY [at] h:mm A')">✓ Signed {{ moment(signer.signature_pandadoc_completed_at).fromNow() }}</span>
                                                </template>
                                                <template v-else>
                                                    <p class="text-stone-500">(Pending signature)</p>
                                                </template>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Timestamps -->
                                    <div class="pt-2 border-t">
                                        <p class="text-lifeworx-blue-900 font-semibold text-xs space-y-1">
                                            <span class="block" :title="moment(displayedRow.data.doc.created_at).utc().format('MMM D, YYYY [at] h:mm A')">{{ displayedRow.data.doc.manual_upload_original_filename ? 'Uploaded' : 'Created' }} {{ moment(displayedRow.data.doc.created_at).fromNow() }}</span>
                                            <span v-if="displayedRow.data.doc.updated_at !== displayedRow.data.doc.created_at" class="block" :title="moment(displayedRow.data.doc.updated_at).utc().format('MMM D, YYYY [at] h:mm A')">Updated {{ moment(displayedRow.data.doc.updated_at).fromNow() }}</span>
                                        </p>
                                    </div>

                                    <!-- Bundle ID -->
                                    <div class="border-b pb-3">
                                        <p class="text-xs font-semibold text-lifeworx-blue-900/50">File Bundle ID {{ displayedRow.data.bundle.id }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- File actions -->
                            <div class="absolute bottom-14 w-1/3 bg-stone-100 border-t border-stone-300 flex-shrink-0">
                                <div :class="['grid grid-cols-2', !displayedRow.isPinned && 'opacity-50 pointer-events-none select-none']">
                                    <button
                                        v-if="canPreviewFile(displayedRow.data.doc)"
                                        type="button"
                                        @click.prevent="handlePreviewDocument(displayedRow.data.doc)"
                                        class="flex items-center justify-center gap-1 py-3 text-xs font-semibold text-stone-600 hover:bg-stone-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none transition border-r border-b border-stone-300 select-none"
                                    >
                                        <EyeIcon class="size-4" />
                                        Preview
                                    </button>
                                    <button
                                        type="button"
                                        @click.prevent="handleDownloadDocument(displayedRow.data.doc)"
                                        :disabled="!displayedRow.data.doc?.download_url"
                                        class="flex items-center justify-center gap-1 py-3 text-xs font-semibold text-stone-600 hover:bg-stone-300 transition border-b border-stone-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none select-none"
                                        :style="!displayedRow.data.doc?.download_url ? { color: '#9ca3af' } : { color: '#1f2937' }"
                                        :class="[ canPreviewFile(displayedRow.data.doc) ? '' : 'col-span-2' ]"
                                    >
                                        <ArrowDownTrayIcon class="size-4" />
                                        Download
                                    </button>
                                    <button
                                        v-if="!['uploaded'].includes(displayedRow.type)"
                                        type="button"
                                        @click.prevent="cancelSigningRequest(displayedRow.data.bundle)"
                                        :disabled="displayedRow.type !== 'pending' || isCanceling"
                                        :class="[
                                            'flex col-span-2 items-center justify-center gap-1 py-3 text-xs font-semibold text-lifeworx-red-500 hover:bg-lifeworx-red-50 disabled:text-lifeworx-red-600/50 disabled:pointer-events-none disabled:cursor-not-allowed transition border-r border-stone-300 select-none',
                                            displayedRow.data.bundle?.signature_canceled_at ? '' : 'hover:bg-stone-300',
                                        ]"
                                    >
                                        <XCircleIcon class="size-4" />
                                        <span v-if="isCanceling && displayedRow.type === 'pending'">Canceling…</span>
                                        <span v-else-if="displayedRow.data.bundle?.signature_canceled_at">Canceled</span>
                                        <span v-else>Cancel Document Request</span>
                                    </button>
                                    <button
                                        v-else
                                        type="button"
                                        @click.prevent="deleteDocument(displayedRow.data.doc, displayedRow.data.bundle)"
                                        :disabled="isDeleting"
                                        class="flex col-span-2 items-center justify-center gap-1 py-3 text-xs font-semibold text-lifeworx-red-500 hover:bg-lifeworx-red-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition select-none"
                                    >
                                        <TrashIcon class="size-4" />
                                        <span v-if="isDeleting">Deleting…</span>
                                        <span v-else>Delete</span>
                                    </button>
                                </div>

                                <!-- Overlay shown when row is not pinned -->
                                <Transition
                                    enter-active-class="transition-opacity duration-150"
                                    leave-active-class="transition-opacity duration-150"
                                    enter-from-class="opacity-0"
                                    leave-to-class="opacity-0"
                                >
                                    <div
                                        v-if="!displayedRow.isPinned"
                                        class="absolute inset-0 flex items-center justify-center backdrop-blur-[1.5px] bg-stone-200/40 cursor-default"
                                    >
                                        <span class="text-[11px] font-semibold text-stone-500 select-none">Click to pin for options</span>
                                    </div>
                                </Transition>
                            </div>
                        </template>

                        <div v-else class="justify-center flex flex-col items-center flex-1">
                            <span class="text-stone-400 text-xs px-6 py-4 block">No Document Selected</span>
                        </div>
                    </div>
                </div>

                <!-- Tip bar -->
                <div class="flex border-t items-center px-6 h-14 flex-shrink-0">
                    <div class="flex-auto pt-0.5 text-sm">
                        <span class="font-semibold text-lifeworx-blue-900 ">Tip: </span>
                        <span class=" text-lifeworx-blue-700">Document requirements are determined by profile details, make sure they're up to date.</span>
                    </div>
                </div>
            </template>
        </div>

        <LatteClientSignatureRequestDialog
            ref="signature-request-dialog"
            :show="signatureRequestOpen"
            :agency-options="agencyOptions"
            :document-options="documentOptions"
            :staff-options="staffOptions"
            :initial-selected-document-ids="missingDocuments.map(d => d.id)"
            @close="closeSignatureRequestDialog"
            :submitting="isSignatureBundleSubmitting"
            @submit="handleSignatureRequestSubmit"
            @start-tour-at-step="startDocumentsTour"
        />

        <Modal
            :show="showMissingSignerEmailConfirmation"
            max-width="xl"
            @close="showMissingSignerEmailConfirmation = false"
        >
            <div class="pb-4">
                <div class="sm:flex sm:items-start">
                    <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-lifeworx-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                        <ExclamationTriangleIcon
                            class="h-6 w-6 text-lifeworx-orange-600"
                            aria-hidden="true"
                        />
                    </div>
                    <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <h3 class="text-lg pt-1.5 pb-3 font-semibold leading-6 text-lifeworx-blue-800">
                            Primary or Billing Contact(s) Missing Email
                        </h3>
                        <div class="mt-2">
                            <p class="text-sm text-stone-500">
                                The following primary or billing contact(s) do not have a valid primary email and will be skipped:
                            </p>
                            <ul class="mt-2 list-disc list-inside text-sm font-bold text-stone-600">
                                <li v-for="(name, idx) in missingSignerNames" :key="idx">{{ name }}</li>
                            </ul>
                            <p class="mt-2 text-sm text-stone-500">
                                You can go back to add emails, or continue and Start Signature Request anyway.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="sm:flex sm:flex-row-reverse">
                <PrimaryButton
                    class="w-full sm:w-auto sm:ml-3"
                    button_style="text-white bg-lifeworx-green-600 hover:bg-lifeworx-green-500 focus:bg-lifeworx-green-600 active:bg-lifeworx-green-700 focus:ring-lifeworx-green-400"
                    @click="continueStartSignature"
                >
                    Start Signature Request
                </PrimaryButton>

                <SecondaryButton
                    class="mt-3 w-full sm:mt-0 sm:w-auto"
                    @click="handleMissingEmailConfirmationClose"
                >
                    Go Back &amp; Resolve
                </SecondaryButton>
            </div>
        </Modal>

        <ConfirmationModal
            :show="confirmingDocumentDeletion"
            title="Are you sure you want to delete this document?"
            message="This action cannot be undone."
            :action-label="isDeleting ? 'Deleting…' : 'Yes'"
            cancel-label="No"
            :action-is-danger="true"
            :action-disabled="isDeleting"
            @confirm="confirmDeleteDocument"
            @close="confirmingDocumentDeletion = false"
        />

        <ConfirmationModal
            :show="confirmingCancelSigning"
            title="Are you sure you want to cancel this bundled document signing request? All included documents will be canceled."
            message="This action cannot be undone."
            :action-label="isCanceling ? 'Canceling…' : 'Yes'"
            cancel-label="No"
            :action-is-danger="true"
            :action-disabled="isCanceling"
            @confirm="confirmCancelSigningRequest"
            @close="confirmingCancelSigning = false"
        />

        <DocumentTagModal
            :show="tagModalOpen"
            :document="selectedDocument"
            :bundle="selectedBundle"
            :tags="documentTags"
            :saveLoading="tagSaveLoading"
            :initialTags="tagModalInitialTags"
            @close="handleTagModalClose"
            @submit="submitTagUpdate"
        />

    </div>

</template>

